import { useQuery } from '@tanstack/react-query';
import { cdApi } from '../lib/api';
import type { Masseuse } from '../types';

// Re-export type for convenience
export type { Masseuse };

/**
 * Query key for available masseuses
 * Using a simple constant since no filters are needed
 */
const AVAILABLE_MASSEUSES_QUERY_KEY = ['masseuses', 'available'] as const;

/**
 * Fetch available masseuses from the API
 */
const fetchAvailableMasseuses = async (): Promise<Masseuse[]> => {
  const { data } = await cdApi.get<Masseuse[]>('/masseuses/available/');
  return data;
};

/**
 * Return type for the useAvailableMasseuses hook
 */
interface UseAvailableMasseusesReturn {
  /** Array of available masseuses */
  masseuses: Masseuse[];
  /** Whether the initial fetch is in progress */
  isLoading: boolean;
  /** Whether any fetch (initial or refetch) is in progress */
  isFetching: boolean;
  /** Error object if the fetch failed */
  error: Error | null;
  /** Function to manually refetch the data */
  refetch: () => Promise<unknown>;
}

/**
 * Custom hook to fetch available masseuses
 *
 * Fetches from GET /cd/api/v1/masseuses/available/
 * No authentication or query parameters required.
 *
 * @returns Query result with available masseuses data, loading state, and error
 *
 * @example
 * const { masseuses, isLoading, error } = useAvailableMasseuses();
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 *
 * return masseuses.map(m => <MasseuseCard key={m.id} masseuse={m} />);
 */
function useAvailableMasseuses(): UseAvailableMasseusesReturn {
  const query = useQuery({
    queryKey: AVAILABLE_MASSEUSES_QUERY_KEY,
    queryFn: fetchAvailableMasseuses,
    // Cache for a long time since available list doesn't change frequently
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  });

  // Sort masseuses: Sensual first (alphabetically), then Shiatsu last (alphabetically)
  // Filter out test masseuses
  const sortedMasseuses = [...(query.data ?? [])]
    .filter((m) => m.name !== 'MasseuseTest')
    .sort((a, b) => {
      // Sensual comes before Shiatsu
      if (a.technique === 'Sensual' && b.technique === 'Shiatsu') return -1;
      if (a.technique === 'Shiatsu' && b.technique === 'Sensual') return 1;
      // Within same technique, sort alphabetically by name
      return (a.name ?? '').localeCompare(b.name ?? '');
    });

  return {
    masseuses: sortedMasseuses,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

export default useAvailableMasseuses;
